<template>
  <div
    class="basket-preview-wrapper h-100"
    :class="{
      empty: !basketItems.length,
      'open-hover': hover,
      'open-right': !hover,
    }"
  >
    <div class="position-relative h-100">
      <div
        class="basket-preview d-flex flex-column flex-nowrap bg-white shadow w-100"
      >
        <header
          class="basket-preview-header border-bottom px-3 py-2 d-flex align-items-center justify-content-between"
        >
          <span class="h3 mb-0">
            {{ $translate("Ceres::Template.basketPreview") }}
          </span>
          <button
            v-toggle-basket-preview
            type="button"
            class="close p-0 m-0"
            :aria-label="$translate('Ceres::Template.closeIcon')"
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                width="40"
                height="40"
                rx="20"
                fill="white"
                fill-opacity="0.68"
              ></rect>
              <path
                d="M28.3332 11.667L11.6665 28.3337M11.6665 11.667L28.3332 28.3337"
                stroke="#090909"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </svg>
          </button>
        </header>

        <div v-if="basketNotifications.length > 0">
          <div
            class="w-100 alert alert-danger"
            v-for="notification in basketNotifications"
            :key="notification.id"
          >
            <div>{{ notification.message }}</div>
          </div>
        </div>

        <div class="basket-preview-content d-flex flex-fill">
          <div
            class="item-list d-flex flex-fill flex-nowrap flex-column overflow-auto px-3"
          >
            <basket-list
              :is-preview="true"
              :multi-language="{ RemoveItem: multiLanguage.RemoveItem }"
            >
              <template #before-basket-item>
                <slot name="before-basket-item"></slot>
              </template>
              <template #after-basket-item>
                <slot name="after-basket-item"></slot>
              </template>
            </basket-list>

            <template v-if="basketItems.length > 0">
              <hr />
              <div class="basket-footer">
                <div class="net-total d-flex justify-content-between my-1">
                  <p class="small">
                    {{ $translate("Ceres::Template.basketValue") }}
                  </p>
                  <strong>
                    {{
                      (showNetPrices ? basket.itemSumNet : basket.itemSum)
                        | currency
                    }}
                  </strong>
                </div>
                <div
                  class="shipping-net-total d-flex justify-content-between my-1"
                >
                  <p class="small">
                    {{ $translate("Ceres::Template.basketShippingCosts") }}
                  </p>
                  <strong>
                    {{
                      (showNetPrices
                        ? basket.shippingAmountNet
                        : basket.shippingAmount) | currency
                    }}
                  </strong>
                </div>
                <div
                  v-if="basket.couponCode && basket.couponDiscount"
                  class="shipping-coupon-discount d-flex justify-content-between my-1"
                >
                  <p class="small">
                    {{ $translate("Ceres::Template.basketCoupon") }}
                  </p>
                  <strong>
                    {{ basket.couponDiscount | currency }}
                  </strong>
                </div>
                <div
                  class="final-net-total d-flex align-items-center justify-content-between my-1"
                >
                  <p class="h5">
                    {{ $translate("Ceres::Template.basketTotalSum") }}
                    <span class="small text-muted mr-2">
                      ({{ $translate("Ceres::Template.basketVAT") }})
                    </span>
                  </p>
                  <strong>
                    {{
                      (showNetPrices
                        ? basket.basketAmountNet
                        : basket.basketAmount) | currency
                    }}
                  </strong>
                </div>
              </div>
            </template>

            <hr />

            <div class="basket-cross-products">
              <p class="h5 font-weight-medium">
                {{ multiLanguage.BasketCrossSellingTitle }}
              </p>
              <div class="cross-products mt-3">
                <div class="cross-product d-flex my-3">
                  <a
                    :href="multiLanguage.ProductLink1"
                    :aria-label="multiLanguage.ProductTitle1"
                  >
                    <lazy-img
                      ref="itemLazyImage"
                      image-url="https://cdn02.plentymarkets.com/6wg68w8zahno/item/images/12406442/preview/TPU-130x60---1.jpg"
                      :alt="multiLanguage.ProductTitle1"
                      :title="multiLanguage.ProductTitle1"
                      picture-class="img-fluid"
                    ></lazy-img>
                  </a>
                  <div class="ml-3 cross-product-info">
                    <p class="title font-weight-medium">
                      {{ multiLanguage.ProductTitle1 }}
                    </p>
                    <select
                      class="size text-muted"
                      @change="selectedCrossProduct[1] = $event.target.value"
                    >
                      <option
                        v-for="item in crossProducts[1]"
                        :key="item.id"
                        :value="item.id"
                        :selected="
                          parseInt(selectedCrossProduct[1]) === item.id
                        "
                      >
                        {{ item.size }}
                      </option>
                    </select>
                    <p class="price font-weight-bold">
                      {{ selectedProductPrice1 | currency }}
                    </p>
                    <button
                      ref="button1"
                      :aria-label="multiLanguage.BasketAdd"
                      @click="addToCart(1)"
                      class="mt-2 add-to-basket d-flex align-items-center"
                    >
                      <span class="mr-1">{{ multiLanguage.BasketAdd }}</span>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.00014 14H18.1359C19.1487 14 19.6551 14 20.0582 13.8112C20.4134 13.6448 20.7118 13.3777 20.9163 13.0432C21.1485 12.6633 21.2044 12.16 21.3163 11.1534L21.9013 5.88835C21.9355 5.58088 21.9525 5.42715 21.9031 5.30816C21.8597 5.20366 21.7821 5.11697 21.683 5.06228C21.5702 5 21.4155 5 21.1062 5H4.50014M2 2H3.24844C3.51306 2 3.64537 2 3.74889 2.05032C3.84002 2.09463 3.91554 2.16557 3.96544 2.25376C4.02212 2.35394 4.03037 2.48599 4.04688 2.7501L4.95312 17.2499C4.96963 17.514 4.97788 17.6461 5.03456 17.7462C5.08446 17.8344 5.15998 17.9054 5.25111 17.9497C5.35463 18 5.48694 18 5.75156 18H19M7.5 21.5H7.51M16.5 21.5H16.51M8 21.5C8 21.7761 7.77614 22 7.5 22C7.22386 22 7 21.7761 7 21.5C7 21.2239 7.22386 21 7.5 21C7.77614 21 8 21.2239 8 21.5ZM17 21.5C17 21.7761 16.7761 22 16.5 22C16.2239 22 16 21.7761 16 21.5C16 21.2239 16.2239 21 16.5 21C16.7761 21 17 21.2239 17 21.5Z"
                          stroke="#121926"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <div class="cross-product d-flex my-3">
                  <a
                    :href="multiLanguage.ProductLink2"
                    :aria-label="multiLanguage.ProductTitle2"
                  >
                    <lazy-img
                      ref="itemLazyImage"
                      image-url="https://cdn02.plentymarkets.com/6wg68w8zahno/item/images/12421780/preview/Cleanilo-Reinigungsspray-Frontansicht.png"
                      :alt="multiLanguage.ProductTitle2"
                      :title="multiLanguage.ProductTitle2"
                      picture-class="img-fluid"
                    ></lazy-img>
                  </a>
                  <div class="ml-3 cross-product-info">
                    <p class="title font-weight-medium">
                      {{ multiLanguage.ProductTitle2 }}
                    </p>
                    <p class="size text-muted">
                      {{ crossProducts[2][0].size }}
                    </p>
                    <p class="price font-weight-bold">
                      {{ selectedProductPrice2 | currency }}
                    </p>
                    <button
                      ref="button2"
                      :aria-label="multiLanguage.BasketAdd"
                      @click="addToCart(2)"
                      class="mt-2 add-to-basket d-flex align-items-center"
                    >
                      <span class="mr-1">{{ multiLanguage.BasketAdd }}</span>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.00014 14H18.1359C19.1487 14 19.6551 14 20.0582 13.8112C20.4134 13.6448 20.7118 13.3777 20.9163 13.0432C21.1485 12.6633 21.2044 12.16 21.3163 11.1534L21.9013 5.88835C21.9355 5.58088 21.9525 5.42715 21.9031 5.30816C21.8597 5.20366 21.7821 5.11697 21.683 5.06228C21.5702 5 21.4155 5 21.1062 5H4.50014M2 2H3.24844C3.51306 2 3.64537 2 3.74889 2.05032C3.84002 2.09463 3.91554 2.16557 3.96544 2.25376C4.02212 2.35394 4.03037 2.48599 4.04688 2.7501L4.95312 17.2499C4.96963 17.514 4.97788 17.6461 5.03456 17.7462C5.08446 17.8344 5.15998 17.9054 5.25111 17.9497C5.35463 18 5.48694 18 5.75156 18H19M7.5 21.5H7.51M16.5 21.5H16.51M8 21.5C8 21.7761 7.77614 22 7.5 22C7.22386 22 7 21.7761 7 21.5C7 21.2239 7.22386 21 7.5 21C7.77614 21 8 21.2239 8 21.5ZM17 21.5C17 21.7761 16.7761 22 16.5 22C16.2239 22 16 21.7761 16 21.5C16 21.2239 16.2239 21 16.5 21C16.7761 21 17 21.2239 17 21.5Z"
                          stroke="#121926"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="basket-totals bg-white d-flex flex-nowrap flex-column"
            v-if="basketItems.length > 0"
          >
            <div class="basket-buttons d-flex px-3 py-3">
              <a
                :href="$ceres.urls.basket"
                rel="nofollow"
                class="btn btn-block btn-basket font-weight-medium d-flex align-items-center justify-content-center m-0"
                :title="$translate('Ceres::Template.basket')"
              >
                {{ $translate("Ceres::Template.basket") }}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.00014 14H18.1359C19.1487 14 19.6551 14 20.0582 13.8112C20.4134 13.6448 20.7118 13.3777 20.9163 13.0432C21.1485 12.6633 21.2044 12.16 21.3163 11.1534L21.9013 5.88835C21.9355 5.58088 21.9525 5.42715 21.9031 5.30816C21.8597 5.20366 21.7821 5.11697 21.683 5.06228C21.5702 5 21.4155 5 21.1062 5H4.50014M2 2H3.24844C3.51306 2 3.64537 2 3.74889 2.05032C3.84002 2.09463 3.91554 2.16557 3.96544 2.25376C4.02212 2.35394 4.03037 2.48599 4.04688 2.7501L4.95312 17.2499C4.96963 17.514 4.97788 17.6461 5.03456 17.7462C5.08446 17.8344 5.15998 17.9054 5.25111 17.9497C5.35463 18 5.48694 18 5.75156 18H19M7.5 21.5H7.51M16.5 21.5H16.51M8 21.5C8 21.7761 7.77614 22 7.5 22C7.22386 22 7 21.7761 7 21.5C7 21.2239 7.22386 21 7.5 21C7.77614 21 8 21.2239 8 21.5ZM17 21.5C17 21.7761 16.7761 22 16.5 22C16.2239 22 16 21.7761 16 21.5C16 21.2239 16.2239 21 16.5 21C16.7761 21 17 21.2239 17 21.5Z"
                    stroke="#121926"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </a>

              <a
                :href="$ceres.urls.checkout"
                rel="nofollow"
                class="btn btn-block btn-checkout font-weight-medium d-flex align-items-center justify-content-center m-0"
                :title="$translate('Ceres::Template.basketCheckout')"
              >
                {{ $translate("Ceres::Template.basketCheckout") }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="basket-preview-overlay" v-toggle-basket-preview></div>
  </div>
</template>

<script>
import ApiService from "../../services/ApiService";
import { mapState } from "vuex";

export default {
  name: "basket-preview",
  props: {
    showNetPrices: {
      type: Boolean,
      default: false,
    },
    currency: {
      type: String,
      default: "EUR",
    },
    multiLanguage: Object,
  },

  data() {
    return {
      exchangeRate: 1,
      crossProducts: {
        1: [
          { id: 133012, price: 12, size: "130 x 60 cm" },
          { id: 133013, price: 15, size: "150 x 80 cm" },
          { id: 133014, price: 23, size: "190 x 120 cm" },
          { id: 133015, price: 33, size: "230 x 160 cm" },
          { id: 133016, price: 43, size: "290 x 190 cm" },
          { id: 133017, price: 61, size: "340 x 240 cm" },
        ],
        2: [{ id: 164974, price: 29, size: "250 ml" }],
      },
      selectedCrossProduct: {
        1: 133012,
        2: 164974,
      },
    };
  },

  computed: {
    hover() {
      return App.config.basket.previewType === "hover";
    },

    selectedProductPrice1() {
      return this.getProductPrice(1);
    },
    selectedProductPrice2() {
      return this.getProductPrice(2);
    },

    showShippingCountrySelect() {
      return App.config.basket.showShippingCountrySelect;
    },

    ...mapState({
      basket: (state) => state.basket.data,
      basketItems: (state) => state.basket.items,
      basketNotifications: (state) => state.basket.basketNotifications,
      redeemedCouponCode: (state) => state.basket.data.couponCode,
      isBasketItemQuantityUpdate: (state) =>
        state.basket.isBasketItemQuantityUpdate,
    }),
  },

  mounted() {
    this.$nextTick(() => {
      if (
        this.basket &&
        this.basket.itemSum != null &&
        this.basket.couponCode != null
      ) {
        this.updateUptainData();
      }
    });

    if (App.config.basket.addItemToBasketConfirm === "preview") {
      ApiService.listen("AfterBasketItemAdd", (data) => {
        this.show();
      });

      ApiService.listen("AfterBasketItemUpdate", (data) => {
        if (!this.isBasketItemQuantityUpdate) {
          this.show();
        }
      });
    }

    this.getData();
  },

  watch: {
    "basket.itemSum": {
      immediate: true,
      handler(newVal) {
        if (newVal != null) this.updateUptainData();
      },
    },
    "basket.couponCode": {
      immediate: true,
      handler(newVal) {
        if (newVal != null) this.updateUptainData();
      },
    },
  },

  methods: {
    async getData() {
      try {
        const res = await fetch(
          "https://cdn02.plentymarkets.com/6wg68w8zahno/frontend/assets/country-shipping-details.json"
        );
        const finalRes = await res.json();
        this.shippingData = finalRes;

        if (this.$props.currency !== "EUR") {
          this.getCurrencyValue();
        }
      } catch (error) {
        console.log(error);
      }
    },

    async getCurrencyValue() {
      try {
        const response = await fetch(
          "https://morgenland-cpanel.eu/api/morgenland-currencies"
        );
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        const targetCurrency = this.$props.currency;
        const currencyData = data.data.find(
          (currency) => currency.currency === targetCurrency
        );
        if (currencyData && currencyData.exchange_rate) {
          this.exchangeRate = parseFloat(currencyData.exchange_rate);
        } else {
          console.log(`Currency ${targetCurrency} not found`);
        }
      } catch (err) {
        console.error("There was a problem with your fetch operation:", err);
      }
    },

    getProductPrice(productNumber) {
      const selectedId = this.selectedCrossProduct[productNumber];
      const productList = this.crossProducts[productNumber];

      const selectedProduct = productList
        ? productList.find((item) => item.id == parseInt(selectedId))
        : null;

      return this.exchangeRate * (selectedProduct ? selectedProduct.price : 0);
    },

    show() {
      setTimeout(function () {
        document.body.classList.add("basket-open");
      }, 1);
    },

    addToCart(productNumber = 1) {
      window.ceresStore.dispatch("addBasketItem", {
        variationId: this.selectedCrossProduct[productNumber],
        quantity: 1,
      });
    },

    updateUptainData() {
      const uptainDiv = document.getElementById("__up_data_qp");
      if (uptainDiv) {
        uptainDiv.dataset.scv = this.basket.itemSum || "0";
        uptainDiv.dataset.usedvoucher = this.basket.couponCode || "";
        window._upEventBus.publish("uptain.readData");
      }
    },
  },
};
</script>
